import { useState, useCallback, useMemo, useEffect } from 'react';

const useBulkSelection = (items = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Clear selection when items change to prevent stale selections
  useEffect(() => {
    if (items.length === 0) {
      setSelectedIds(new Set());
      return;
    }
    
    // Remove any selected IDs that are no longer in the items array
    setSelectedIds(prev => {
      const itemIds = new Set(items.map(item => item._id).filter(Boolean));
      const newSelection = new Set();
      
      for (const id of prev) {
        if (itemIds.has(id)) {
          newSelection.add(id);
        }
      }
      
      return newSelection;
    });
  }, [items]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);
  
  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);
  
  const isAllSelected = useMemo(() => {
    return items.length > 0 && selectedIds.size === items.length;
  }, [items.length, selectedIds.size]);
  
  const isIndeterminate = useMemo(() => {
    return selectedIds.size > 0 && selectedIds.size < items.length;
  }, [items.length, selectedIds.size]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      // Only add valid IDs
      const validIds = items.map(item => item._id).filter(Boolean);
      setSelectedIds(new Set(validIds));
    }
  }, [items, isAllSelected]);

  const selectRange = useCallback((startId, endId) => {
    const startIndex = items.findIndex(item => item._id === startId);
    const endIndex = items.findIndex(item => item._id === endId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      for (let i = minIndex; i <= maxIndex; i++) {
        if (items[i] && items[i]._id) {
          newSet.add(items[i]._id);
        }
      }
      return newSet;
    });
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectItems = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  const getSelectedItems = useCallback(() => {
    return items.filter(item => item && item._id && selectedIds.has(item._id));
  }, [items, selectedIds]);

  const getSelectedIds = useCallback(() => {
    return Array.from(selectedIds);
  }, [selectedIds]);

  // Bulk operation helpers
  const canPerformBulkActions = useMemo(() => selectedCount > 0, [selectedCount]);
  
  const getSelectedItemsByStatus = useCallback((status) => {
    return getSelectedItems().filter(item => item && item.status === status);
  }, [getSelectedItems]);

  const hasSelectedItemsWithStatus = useCallback((status) => {
    return getSelectedItems().some(item => item && item.status === status);
  }, [getSelectedItems]);

  return {
    // Selection state
    selectedIds,
    selectedCount,
    isSelected,
    isAllSelected,
    isIndeterminate,
    canPerformBulkActions,
    
    // Selection actions
    toggleSelect,
    toggleSelectAll,
    selectRange,
    clearSelection,
    selectItems,
    
    // Getters
    getSelectedItems,
    getSelectedIds,
    getSelectedItemsByStatus,
    hasSelectedItemsWithStatus
  };
};

export default useBulkSelection;